import { Box } from '@mui/material';
import type { Identifier } from 'dnd-core';
import { ReactNode, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { useResizeDetector } from 'react-resize-detector';

import { CustomDragLayer } from './CustomDragLayer';
import { onDnDHover } from './utils';

type DraggableItemProps<T> = {
  index: number;
  renderContent: () => ReactNode;
  onMove: (from: number, to: number) => void;
  onDrop: (item: T) => void;
  accept: string;
};

export const DraggableItem = <T extends { index: number }>({
  index,
  onMove,
  onDrop,
  renderContent,
  accept,
}: DraggableItemProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width: containerWidth, ref: containerRef } = useResizeDetector<HTMLDivElement>();
  const { height, ref: contentRef } = useResizeDetector<HTMLDivElement>();

  const [, drop] = useDrop<T, void, { handlerId: Identifier | null }>({
    accept,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() };
    },
    hover(item: T, monitor) {
      onDnDHover({ item, monitor, ref, index, move: onMove });
    },
    drop(item: T) {
      onDrop(item);
    },
  });

  drop(ref);

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <CustomDragLayer renderContent={renderContent} width={containerWidth ?? 0} />

      <Box ref={ref} height={height}>
        <div ref={contentRef}>{renderContent()}</div>
      </Box>
    </Box>
  );
};
